// Static catalogue for LOT FOR SALE: lot types, budget bands, guides and page addresses.
// Values lifted from the approved Claude Design handoff (prototype/lfs-data.js, README §4).

export type LotType = "residential" | "commercial" | "industrial" | "agricultural"

export interface LotTypeDef {
  id: LotType
  label: string
  icon: string
  fg: string
  bg: string
  def: string
}

export const LOT_TYPES: LotTypeDef[] = [
  { id: "residential", label: "Residential", icon: "home", fg: "#1A5CF0", bg: "#F3F5F8", def: "Land zoned for homes, from subdivision lots to private estates." },
  { id: "commercial", label: "Commercial", icon: "storefront", fg: "#7a3aa8", bg: "#f3ecf9", def: "Land for retail, offices, mixed-use and hospitality." },
  { id: "industrial", label: "Industrial", icon: "factory", fg: "#1449C4", bg: "#EAF0FE", def: "Land for warehouses, logistics and manufacturing." },
  { id: "agricultural", label: "Agricultural", icon: "agriculture", fg: "#3d7a1e", bg: "#edf5e6", def: "Farm and rural land; conversion rules may apply." },
]

// Page addresses (README §4). Pages not yet built resolve to the 404 page until their ticket lands.
export function lotsHref(f: { type?: string; province?: string; city?: string; min?: number; max?: number; q?: string } = {}): string {
  const path = ["/lots", f.type, f.province, f.city].filter(Boolean).join("/")
  const qs = new URLSearchParams()
  if (f.min) qs.set("min", String(f.min))
  if (f.max) qs.set("max", String(f.max))
  if (f.q) qs.set("q", f.q)
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

export const BUDGETS = [
  { label: "Under ₱10M", min: 0, max: 1e7 },
  { label: "₱10M – ₱25M", min: 1e7, max: 2.5e7 },
  { label: "₱25M – ₱50M", min: 2.5e7, max: 5e7 },
  { label: "₱50M – ₱100M", min: 5e7, max: 1e8 },
  { label: "₱100M – ₱500M", min: 1e8, max: 5e8 },
  { label: "₱500M and above", min: 5e8, max: 0 },
].map((b) => ({ ...b, href: lotsHref({ min: b.min || undefined, max: b.max || undefined }) }))

// Only guides whose full text exists are published (bodies in lib/lfs/guides.ts). The others stay listed
// as drafts: the handoff left their text to the CMS and none exists yet, so they are never linked.
const PUBLISHED = new Set(["price-per-sqm", "due-diligence", "cavite-residential"])

const ALL_GUIDES = [
  { slug: "price-per-sqm", title: "How to Calculate a Lot’s Price per Square Meter", topic: "Pricing", mins: 5, photo: "/lfs/makati.jpg",
    dek: "The formula, a worked example, and why price per sqm is the fastest way to compare lots of different sizes." },
  { slug: "due-diligence", title: "What to Verify Before Buying a Lot in the Philippines", topic: "Due diligence", mins: 8, photo: "/lfs/field-parcel.jpg",
    dek: "A document checklist — title, tax declaration, survey plan, zoning — and where each one comes from." },
  { slug: "cavite-residential", title: "Residential Lots in Cavite: What Current Listings Show", topic: "Local markets", mins: 4, photo: "/lfs/tagaytay.jpg",
    dek: "Asking prices and lot sizes across our Cavite inventory, refreshed from live listings." },
  { slug: "residential-vs-commercial", title: "Residential vs Commercial Land: What’s the Difference?", topic: "Buying guides", mins: 6, photo: "/lfs/metro-manila.jpg",
    dek: "How land classification affects what you can build, how you finance it and what you pay in tax." },
  { slug: "buying-a-lot", title: "Buying a Lot in the Philippines, Step by Step", topic: "Buying guides", mins: 9, photo: "/lfs/rice-house.jpg",
    dek: "From shortlisting and site visits to the deed of sale and title transfer." },
  { slug: "gated-village", title: "Buying a Lot in a Gated Village: Clearances and Deed Restrictions", topic: "Due diligence", mins: 6, photo: "/lfs/forbes-park.jpg",
    dek: "Association clearances, building restrictions and dues to check before you reserve." },
  { slug: "agricultural-land", title: "Buying Agricultural Land: Use, Conversion and DAR", topic: "Due diligence", mins: 7, photo: "/lfs/rice-farm.jpg",
    dek: "What agricultural classification means for buyers and when conversion is required." },
].map((g) => ({ ...g, href: `/insights/${g.slug}` }))

export const GUIDES = ALL_GUIDES.filter((g) => PUBLISHED.has(g.slug))
export const DRAFT_GUIDES = ALL_GUIDES.filter((g) => !PUBLISHED.has(g.slug))

export const ROUTES = {
  home: "/",
  lots: "/lots",
  locations: "/locations",
  villages: "/locations#villages",
  developments: "/locations#developments",
  insights: "/insights",
  about: "/about",
  contact: "/contact",
  privacy: "/privacy",
  terms: "/terms",
  disclosures: "/disclosures",
}

// Inquiry popup triggers (README §5): one popup, three modes, opened by URL hash.
export const INQUIRE = {
  general: "#inquire=general",
  buy: "#inquire=general&intent=buy",
  sell: "#inquire=sell",
  property: (ref: string) => `#inquire=property&ref=${encodeURIComponent(ref)}`,
}
